// Conjunctions — the joiners. Coordinating (e, ou, mas, mas sim) and
// subordinating (que, porque, embora, quando, se…), plus the moods they trigger.
// A reference table groups the common ones at the bottom.
import { renderGuideSections } from "../ui/guideSections.js";

const COORD = [
  ["e", "and", "café e chá"],
  ["ou", "or", "um ou dois"],
  ["mas", "but", "quero mas não posso"],
  ["mas sim", "but rather", "não é vermelho mas sim azul"],
  ["nem", "nor", "nem tu nem eu"],
];
const SUBORD = [
  ["que", "that", "acho que sim"],
  ["porque", "because", "porque quero"],
  ["embora", "although / even though", "embora chova"],
  ["quando", "when", "quando chegares"],
  ["se", "if", "se puderes"],
  ["enquanto", "while", "enquanto estudo"],
  ["para que", "so that", "para que aprendas"],
  ["como", "as / since", "como é tarde"],
];

export function conjunctionsView(app) {
  const block = (title, rows) => `
    <div class="bg-white rounded-2xl border border-sage-100 p-5">
      <h3 class="text-lg font-bold text-sage-800 mb-3">${title}</h3>
      <div class="overflow-x-auto"><table class="w-full text-sm">
        <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
          <th class="py-2 pr-3">Portuguese</th><th class="py-2 px-3">English</th><th class="py-2 px-3">Example</th>
        </tr></thead>
        <tbody>
          ${rows
            .map(
              ([pt, en, ex]) => `<tr class="border-b border-sage-50 last:border-0">
            <td class="py-2 pr-3 font-medium text-sage-800">${pt}</td>
            <td class="py-2 px-3 text-sage-700">${en}</td>
            <td class="py-2 px-3 text-sage-500 italic">${ex}</td>
          </tr>`
            )
            .join("")}
        </tbody>
      </table></div>
    </div>`;

  const table = `
    <div class="grid gap-4 mt-6">
      ${block("Coordinating", COORD)}
      ${block("Subordinating", SUBORD)}
    </div>`;

  renderGuideSections(
    app,
    "Conjunctions",
    "Conjunctions glue words, phrases, and clauses together. The coordinating ones join equals; the subordinating ones hang one idea off another — and several of them trigger the subjunctive.",
    [
      {
        title: "1. Coordinating conjunctions",
        body: `
          <p class="text-sage-700 mb-2">Coordinators link items of equal weight — and (e), or (ou), but (mas), nor (nem):</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>e</b> (and): <i>pão <b>e</b> manteiga</i> — in a negative list it becomes <b>nem</b>: <i>não tenho tempo <b>nem</b> dinheiro</i>.</li>
            <li><b>ou</b> (or): <i>chá <b>ou</b> café</i>; <i><b>ou</b>… <b>ou</b>…</i> for "either… or".</li>
            <li><b>nem</b> (nor / not even): <i><b>nem</b> tu <b>nem</b> eu</i>.</li>
          </ul>
        `,
      },
      {
        title: "2. mas vs mas sim",
        body: `
          <p class="text-sage-700 mb-2">Both relate to <i>but</i>, but they aren't interchangeable.</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>mas</b> adds a contrasting idea: <i>É caro <b>mas</b> bom.</i></li>
            <li><b>mas sim</b> corrects a negative — "not X but (rather) Y": <i>Não é caro <b>mas sim</b> barato.</i></li>
            <li>For a more formal contrast use <b>porém</b> or <b>contudo</b> (however): <i>É caro; <b>porém</b>, vale a pena.</i></li>
          </ul>
        `,
      },
      {
        title: "3. Conjunctions that trigger the subjunctive",
        body: `
          <p class="text-sage-700 mb-2">Some subordinators introduce something not-yet-real, so the following verb goes subjunctive:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>para que</b> (so that): <i>Digo-te <b>para que</b> o <b>saibas</b>.</i> (present subjunctive)</li>
            <li><b>quando</b> + future event: <i>Quando <b>chegares</b>, liga-me.</i> (future subjunctive)</li>
            <li><b>embora</b> (although): <i>Embora <b>chova</b>, vou sair.</i> (even though it's raining)</li>
            <li><b>antes que</b>: <i>antes que <b>seja</b> tarde.</i></li>
          </ul>
        `,
      },
    ],
    "",
    table
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
